import type {
  CasinoGameItem,
  GamesRequestPayload,
} from '@kira-dancer/nuxt-common/types/game'
import { searchStore } from '~/stores/search'
import { LOBBY_GAME_LIMIT } from '~/constants/game'
import { GAME_CASINO_LIMIT } from '~/constants/casino'
import { RoutesEnum } from '~/enums'

export function useSearch() {
  const { lobbyGames, fetchGames } = useLobbyGames('search')
  const route = useRoute()
  const router = useRouter()
  const store = searchStore()
  const { isMobile } = useDevice()

  const params = reactive<GamesRequestPayload>({
    search: '',
  })
  const liveCasinoGames = reactive<CasinoGameItem[]>([])

  const { fetchLiveCasino } = useCasino({
    gameLimit: GAME_CASINO_LIMIT,
  })
  const isLoading = ref(true)

  const totalResults = computed(() => {
    if (!route.query.q) {
      return 0
    }
    return liveCasinoGames?.length + (lobbyGames.value?.length || 0)
  })

  const titleHeader = computed(() => {
    if (isLoading.value) {
      return 'Đang tìm kiếm...'
    }

    const searchQuery = route.query.q || ''
    const maxLength = isMobile ? 25 : 40
    const trimmedQuery =
      searchQuery.length > maxLength
        ? `${searchQuery.slice(0, maxLength)}...`
        : searchQuery

    return `${trimmedQuery} (${totalResults.value} kết quả)`
  })

  const isEmpty = computed(() => totalResults.value == 0 && !isLoading.value)

  const keywordSearch = computed(() => {
    return store.getSearchText()
  })

  const handleSearch = async (keywords: string): Promise<void> => {
    const trimmedKeywords = keywords.trim()
    if (trimmedKeywords) {
      params.search = trimmedKeywords
      setSearchText(trimmedKeywords)
      isLoading.value = true
      try {
        liveCasinoGames.splice(0)
        const results = await Promise.all([
          fetchGames({
            search: trimmedKeywords,
          }),
          fetchLiveCasino({
            search: trimmedKeywords,
          }),
        ])
        const dataCasino = results[1] as CasinoGameItem[]
        liveCasinoGames.push(...dataCasino)
      } catch (ex) {
        console.error('handleSearch', ex)
      } finally {
        isLoading.value = false
      }
    }
  }

  const setSearchText = (keywords: string): void => {
    store.setSearchText(keywords)
  }

  const onChangeSearch = (search: string) => {
    router.replace({
      path: RoutesEnum.Search,
      query: {
        q: search,
      },
    })
  }

  return {
    store,
    titleHeader,
    isEmpty,
    lobbyGames,
    keywordSearch,
    LOBBY_GAME_LIMIT,
    isLoading,
    totalResults,
    liveCasinoGames,
    onChangeSearch,
    handleSearch,
    setSearchText,
  }
}
